import Dexie, {type Table} from 'dexie';
import type {ReplayOutboxScope} from './sessionReplayOutbox';
import type {ReplaySessionResponse} from '../rest/sessionReplayTransport';

export interface ReplayCreationDescriptor extends ReplayOutboxScope {
    initialUrl: string;
    userAgent: string;
    viewportWidth: number;
    viewportHeight: number;
    visitId: string | null;
    /** Original part of this tab's recording; separate tabs have separate roots. */
    continuationOf?: string | null;
    partIndex?: number;
}

export interface ReplayCaptureState {
    sessionId: string;
    identity: string;
    descriptor: ReplayCreationDescriptor;
    confirmed: boolean;
    serverStatus?: 'recording' | 'completed' | 'error';
    phase: 'pending' | 'recording' | 'closing' | 'interrupted';
    summary?: unknown;
    /** Server-confirmed terminal receipt. Persisted before deleting the outbox
     * so cleanup can resume with the original totals after any crash/failure. */
    completion?: ReplaySessionResponse;
    retryAt?: number;
}

class StateDatabase extends Dexie {
    sessions!: Table<ReplayCaptureState, string>;

    constructor(name: string) {
        super(name);
        this.version(1).stores({sessions: 'sessionId, [identity+sessionId]'});
    }
}

const identityOf = ({firmId, userId}: Pick<ReplayOutboxScope, 'firmId' | 'userId'>) => JSON.stringify([firmId, userId]);

/** Creation identity is durable before POST. A lost response therefore retries
 * the same ID and descriptor; it never silently mints a second server session. */
export class ReplayStateStore {
    private readonly db: StateDatabase;

    constructor(databaseName = 'SessionReplayCaptureStateV2') {
        this.db = new StateDatabase(databaseName);
    }

    close(): void { this.db.close(); }

    async prepare(descriptor: ReplayCreationDescriptor): Promise<ReplayCaptureState> {
        if ([descriptor.sessionId, descriptor.firmId, descriptor.userId].some(value => !value)) {
            throw new Error('Replay creation requires a complete identity.');
        }
        // Freeze before the asynchronous transaction, including the caller's
        // original URL/viewport. Later page changes must not alter a retry.
        const copy = {...descriptor};
        const identity = identityOf(copy);
        return this.db.transaction('rw', this.db.sessions, async () => {
            const existing = await this.db.sessions.get(copy.sessionId);
            if (existing) {
                if (existing.identity !== identity || Object.keys(copy).some(key => copy[key] !== existing.descriptor[key])) {
                    throw new Error('Replay creation identity or descriptor changed.');
                }
                return existing;
            }
            const state: ReplayCaptureState = {
                sessionId: copy.sessionId, identity, descriptor: copy, confirmed: false, phase: 'pending',
            };
            await this.db.sessions.add(state);
            return state;
        });
    }

    async get(scope: ReplayOutboxScope): Promise<ReplayCaptureState | undefined> {
        const state = await this.db.sessions.get(scope.sessionId);
        if (state && state.identity !== identityOf(scope)) throw new Error('Replay belongs to another identity.');
        return state;
    }

    async confirm(scope: ReplayOutboxScope, response: unknown): Promise<ReplayCaptureState> {
        const value = response as Record<string, unknown>;
        if (!value || value.protocolVersion !== 2 || value.id !== scope.sessionId ||
            value.firmId !== scope.firmId || value.userId !== scope.userId ||
            !['recording', 'completed', 'error'].includes(String(value.status))) {
            throw new Error('Replay server did not confirm the expected recording identity.');
        }
        return this.change(scope, state => ({
            ...state, confirmed: true,
            serverStatus: value.status as ReplayCaptureState['serverStatus'],
            phase: state.phase === 'closing' ? 'closing' : value.status === 'recording' ? 'recording' : 'interrupted',
        }));
    }

    async mark(scope: ReplayOutboxScope, phase: 'closing' | 'interrupted', summary?: unknown, retryAt?: number): Promise<void> {
        await this.change(scope, state => ({
            ...state,
            // Closing is a durable owner decision. Network/auth/storage
            // interruptions may delay it, but must never turn it back into a
            // resumable recording on the next reload.
            phase: phase === 'interrupted' && state.phase === 'closing' ? 'closing' : phase,
            ...(summary === undefined ? {} : {summary}),
            ...(retryAt === undefined ? {} : {retryAt}),
        }));
    }

    async recordCompletion(scope: ReplayOutboxScope, response: ReplaySessionResponse): Promise<void> {
        if (response.protocolVersion !== 2 || response.id !== scope.sessionId ||
            response.firmId !== scope.firmId || response.userId !== scope.userId || response.status !== 'completed') {
            throw new Error('Replay completion did not confirm the expected recording identity.');
        }
        await this.change(scope, state => {
            if (!state.confirmed || state.phase !== 'closing') throw new Error('Replay completion was not prepared.');
            return {...state, serverStatus: 'completed', completion: {...response}};
        });
    }

    /** Recovery is explicitly scoped and paged; never opens another account's queue. */
    async pending(identity: Pick<ReplayOutboxScope, 'firmId' | 'userId'>, after = '', limit = 20): Promise<ReplayCaptureState[]> {
        if (!Number.isSafeInteger(limit) || limit < 1 || limit > 20) throw new Error('Invalid replay recovery page.');
        const key = identityOf(identity);
        return this.db.sessions.where('[identity+sessionId]')
            .between([key, after], [key, Dexie.maxKey], false, true).limit(limit).toArray();
    }

    /** Only call after the outbox is empty and server completion is confirmed. */
    async complete(scope: ReplayOutboxScope): Promise<void> {
        await this.db.transaction('rw', this.db.sessions, async () => {
            const state = await this.get(scope);
            if (!state) return;
            if (!state.confirmed || state.phase !== 'closing') throw new Error('Replay completion was not prepared.');
            await this.db.sessions.delete(scope.sessionId);
        });
    }

    /** Remove one exact local state after either an authoritative remote
     * disposal or proof that an unconfirmed creation accepted no events. */
    async discard(scope: ReplayOutboxScope): Promise<void> {
        await this.db.transaction('rw', this.db.sessions, async () => {
            const state = await this.get(scope);
            if (state) await this.db.sessions.delete(scope.sessionId);
        });
    }

    private async change(scope: ReplayOutboxScope, update: (state: ReplayCaptureState) => ReplayCaptureState): Promise<ReplayCaptureState> {
        return this.db.transaction('rw', this.db.sessions, async () => {
            const state = await this.get(scope);
            if (!state) throw new Error('Replay creation was not saved.');
            const next = update(state);
            await this.db.sessions.put(next);
            return next;
        });
    }
}
